<template>
    <div class="bill-to-pax">
      <h5 class="bill-to-pax__title">Intestatario ricevuta</h5>

      <loader v-if="loading" />

      <template v-else>
        <div
          v-for="option in holder_options"
          :key="option.key"
          class="bill-to-pax__holder"
          @click="select_holder(option.key)"
        >
          <div class="bill-to-pax__holder-icons">
            <span class="bill-to-pax__holder-icon bill-to-pax__holder-icon--off"></span>
            <vb-icon
              v-show="type_holder === option.key"
              class="bill-to-pax__holder-icon bill-to-pax__holder-icon--on"
              :name="'check_circle'"
              :color="sass('success')"
              :size="18"
            />
          </div>
          <span
            class="bill-to-pax__holder-label"
            :class="(type_holder === option.key) && 'semibold'"
          >
            {{ option.label }}
          </span>
        </div>

        <validation-observer
          v-slot="{ invalid }"
          ref="observer"
          :key="'observer-' + type_holder"
        >
          <div
            class="bill-to-pax__group-input"
            v-for="(field,key) in guest"
            :key="'field_'+key"
          >
            <validation-provider
              :rules="field.rules"
              :name="key"
              :vid="key"
              v-slot="{ errors, validate, failedRules }"
            >
              <template v-if="field.type === 'input'">
                <label class="vb-label" v-html="field.name" />
                <vb-input
                  @input="validate_fields($event,validate,key)"
                  @change="validate_fields($event,validate,key)"
                  @blur="validate_fields($event,validate,key)"
                  v-model="guest[key].value"
                  :vb_options="{
                    name: key,
                    disabled: readonly_fields.includes(key),
                  }"
                />
                <div v-for="(rule,index) in failedRules" v-bind:key="_uid+index">
                  <span v-if="index!=='required'" class="vee--errors">{{ rule.replace(key, field.name) }}</span>
                </div>
              </template>
            </validation-provider>
          </div>
        </validation-observer>
      </template>
    </div>
</template>

<script>
import * as Validation from '@resources/validation'
import { service_handler } from '@/services'
import { get_config } from '@utilities/commons'
import {
  find_welfare_holder,
  find_beneficiary_by_id,
  extract_holder_form_data,
  clean_copied_fiscal_codes
} from './logic'


export default {
    name: 'checkoutBillToPax',

    components: {
    },

    props: {
      validate_on_mount: {
        type: Boolean,
        default: false,
      },
      beneficiary_id: {
        type: [String, Number],
        default: null,
      },
      traveler_data: {
        type: Object,
        default: null,
      },
      dc_from_state: {
        type: Array,
        default: () => [],
      }
    },

    mounted() {
      this.init_welfare_holder()

      if (this.validate_on_mount) {
        let timer = setInterval(() => {
          if (this.$refs.observer) {
            clearInterval(timer)
            this.$refs.observer.validate().then(res => {
              let payload = res ? this.get_payload() : false
              this.emit_results(payload)
            })
          }
        }, 500)
      }
    },

    data() {
      return {
        loading: true,
        type_holder: 'welfare',
        allow_custom_beneficiary: false,
        beneficiaries: [],
        guest: {
          guestFirstName: { ...Validation.first_name },
          guestLastName: { ...Validation.last_name },
          guestFiscalCode: { ...Validation.bill_to_pax_fiscal_code },
          guestAddress: { ...Validation.address },
          guestCity: { ...Validation.city },
          guestPostalCode: { ...Validation.postal_code },
          guestProvince: { ...Validation.province },
        },
        last_valid_state: null,
      }
    },

    computed: {

      has_discount_codes() {
        return this.dc_from_state && this.dc_from_state.length > 0
      },

      is_welfare() {
        return get_config('integration.welfare')
      },

      only_holder() {
        return this.beneficiaries.length === 1 && this.beneficiaries[0].relation === 'me'
      },

      holder_options() {
        const hide_welfare_option = this.has_discount_codes || !this.is_welfare
        const options = [
          { key: 'welfare', label: 'Titolare del credito welfare' },
          { key: 'traveler', label: 'Viaggiatore' },
          { key: 'other', label: 'Altra persona' },
        ]
        return options.filter(o => {
          if (o.key === 'welfare' && hide_welfare_option) return false
          if (o.key === 'traveler' && this.only_holder && !this.allow_custom_beneficiary) return false
          return true
        })
      },

      // Determina quali campi sono in sola lettura in base al tipo di intestatario.
      // Per 'traveler': il CF è editabile se allowCustomBeneficiary oppure se il beneficiario
      // non ha un CF proprio (attualmente il backend copia il CF del titolare welfare).
      readonly_fields() {
        if (this.type_holder === 'welfare') return ['guestFirstName', 'guestLastName', 'guestFiscalCode']
        if (this.type_holder === 'traveler') {
          const traveler = find_beneficiary_by_id(this.beneficiaries, this.beneficiary_id)
          const traveler_has_fiscal_code = traveler && traveler.fiscalCode
          return (this.allow_custom_beneficiary || !traveler_has_fiscal_code)
            ? ['guestFirstName', 'guestLastName']
            : ['guestFirstName', 'guestLastName', 'guestFiscalCode']
        }
        return []
      },
    },

    watch: {
      // Aggiorna il CF quando cambia il beneficiario selezionato in sidebar-guest.
      // Se allowCustomBeneficiary il CF viene svuotato per permettere l'inserimento manuale
      // (il backend può restituire il CF del titolare welfare anche per beneficiari che non ne hanno uno proprio).
      beneficiary_id(newId) {
        if (this.type_holder === 'traveler') {
          const traveler = find_beneficiary_by_id(this.beneficiaries, newId)
          let fiscalCode = ''
          if (!this.allow_custom_beneficiary && traveler) {
            fiscalCode = traveler.fiscalCode || ''
          }
          this.guest.guestFiscalCode.value = fiscalCode
        }
      },
      traveler_data: {
        deep: true,
        handler(newData) {
          if (this.type_holder === 'traveler' && newData) {
            this.guest.guestFirstName.value = newData.firstName
            this.guest.guestLastName.value = newData.lastName
          }
        }
      },
      guest: {
        deep: true,
        handler() {
          this.$nextTick(() => {
            if (!this.$refs.observer) return
            this.$refs.observer.validate().then(res => {
              let payload = res ? this.get_payload() : false
              this.emit_results(payload)
            })
          })
        }
      }
    },

    methods: {

      get_payload() {
        let payload = {}
        for (const key of Object.keys(this.guest)) {
          payload[key] = this.guest[key].value
        }
        return payload
      },

      async validate_fields(value, validate, name) {
        const { valid } = await validate(value)

        if (!valid) {
          this.emit_results(false)
          return
        }

        const fields = this.$refs.observer.fields

        const all_valid = Object.keys(fields).every(key =>
          key === name || fields[key].valid !== false
        )

        this.emit_results(all_valid ? this.get_payload() : false)
      },

      emit_results(payload) {
        this.$emit('change', {name: 'bill-to-pax-update', payload: payload})
        const is_valid = payload !== false
        if (this.last_valid_state !== is_valid) {
          this.last_valid_state = is_valid
          if (is_valid) {
            this.track_form({
              event: 'sidebar-bill-to-pax:update:success',
              data: { type: 'info', holder_type: this.type_holder },
            })
          }
        }
      },

      update_guest_fields(form_data) {
        this.guest.guestFirstName.value = form_data.firstName
        this.guest.guestLastName.value = form_data.lastName
        this.guest.guestFiscalCode.value = form_data.fiscalCode
      },

      select_holder(key) {
        this.type_holder = key

        if (key === 'welfare') {
          const welfare_holder = find_welfare_holder(this.beneficiaries)
          this.update_guest_fields(extract_holder_form_data(welfare_holder))
        } else if (key === 'traveler') {
          const traveler_fields = this.traveler_data || {}
          const traveler = find_beneficiary_by_id(this.beneficiaries, this.beneficiary_id)
          // Se allowCustomBeneficiary svuota il CF per inserimento manuale (vedi commento watcher beneficiary_id)
          this.update_guest_fields({
            firstName: traveler_fields.firstName || '',
            lastName: traveler_fields.lastName || '',
            fiscalCode: this.allow_custom_beneficiary
              ? ''
              : (traveler && traveler.fiscalCode) || ''
          })
        } else {
          this.update_guest_fields(extract_holder_form_data())
        }
      },

      init_welfare_holder() {
        service_handler('beneficiaries', {
          token: this.$config.token,
          routeName: this.$route.name,
        })
        .then(
          (success) => {
            const beneficiaries = clean_copied_fiscal_codes(Object.values(success.data.beneficiaries))
            this.allow_custom_beneficiary = !!success.data.allowCustomBeneficiary
            this.beneficiaries = beneficiaries

            if (this.has_discount_codes || !this.is_welfare) {
              this.select_holder(this.only_holder ? 'other' : 'traveler')
            } else {
              const welfare_holder = find_welfare_holder(beneficiaries)
              this.update_guest_fields(extract_holder_form_data(welfare_holder))
            }

            this.loading = false
          },
          (error) => {
            this.log('[beneficiaries] - error', error)
            this.loading = false
          }
        )
      },
    }
}
</script>

<style lang="scss" scoped>
  .bill-to-pax {

    &__title {
      @extend %sidebar__title;
    }

    &__holder {
      cursor: pointer;
      display: flex;
      align-items: center;
      margin-bottom: $grid-gutter-width / 5;

      &-icons {
        position: relative;
        display: flex;
      }

      &-icon {

        &--off {
          width: 18px;
          height: 18px;
          border: 2px solid $gray-300;
          border-radius: 50%;
          display: inline-block;
        }

        &--on {
          position: absolute;
          top: 0;
          left: 0;
          z-index: 1;
        }
      }

      &-label {
        font-size: $font-size-md;
        margin-left: $grid-gutter-width / 3;
      }
    }

    &__group-input {
      @extend %sidebar__group-input;
    }

}

</style>